import { useCallback, useMemo, useState } from "react";
import { Box, CircularProgress } from "@mui/material";
import { debug as debugFn } from "debug";
import { errAsync, ResultAsync } from "neverthrow";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { match } from "ts-pattern";
import { v4 } from "uuid";

import type { IncidentWorkItemStatus } from "@thalamos/common";
import { IncidentWorkItemStatusSchema } from "@thalamos/common";
import type {
  FormSection,
  IncidentFormId,
  IncidentWorkItemId,
  IncidentWorkItemWithContext,
  RecordPlaceOfSafetyArrivalMinimumFields,
  WorkflowVersion,
} from "@thalamos/vision-common";
import {
  FormSectionSchema,
  getSupportedIncidentTypeSelection,
  IncidentFormIdSchema,
  IncidentWorkItemIdSchema,
  workflows,
} from "@thalamos/vision-common";

import api, { apiHooks } from "../../api/index.js";
import { ArrivePlaceOfSafetyForm } from "../../forms/ArrivePlaceOfSafety/ArrivePlaceOfSafetyForm.js";
import { LeavePlaceOfSafetyForm } from "../../forms/LeavePlaceOfSafety/LeavePlaceOfSafetyForm.js";
import type { RecordPlaceOfSafetyFormProps } from "../../forms/RecordPlaceOfSafety/RecordPlaceOfSafetyForm.js";
import { RecordPlaceOfSafetyForm } from "../../forms/RecordPlaceOfSafety/RecordPlaceOfSafetyForm.js";
import { useToastNotifications } from "../../hooks/useToastNotifications.js";
import { createRoute } from "../../routing/createRoute.js";
import { Error as ErrorComponent } from "../Error/Error.js";

const debug = debugFn("vision-frontend:UpsertPlaceOfSafetyRecordPage");

export type UpsertPlaceOfSafetyRecordPageProps = {
  edit?: boolean;
};

export function UpsertPlaceOfSafetyRecordPage({
  edit,
}: UpsertPlaceOfSafetyRecordPageProps) {
  const params = useParams();
  const navigate = useNavigate();

  const formId = IncidentFormIdSchema.safeParse(params.formId);

  const incidentId = IncidentWorkItemIdSchema.safeParse(params.id);
  if (!incidentId.success) {
    throw new Error("Invalid form ID");
  }

  // Form ID is only required in edit mode
  if (edit && !formId.success) {
    throw new Error("Invalid form ID");
  }

  const [
    {
      data: incidentWorkItem,
      loading: isIncidentLoading,
      error: incidentError,
    },
    refetchIncidentWorkItem,
  ] = apiHooks.incidentWorkItem.get(incidentId.data);

  if (incidentError) {
    return <ErrorComponent />;
  }

  if (isIncidentLoading) {
    return <CircularProgress />;
  }

  return (
    <>
      {incidentWorkItem && (
        <UpsertPlaceOfSafetyRecordPageInner
          incidentWorkItem={incidentWorkItem}
          refetch={async () => {
            await refetchIncidentWorkItem();
          }}
          edit={edit}
          existingFormId={edit ? formId.data : undefined}
          onUpsertPlaceOfSafety={async (incidentWorkItemId, formId, body) => {
            const response = await api.incidentWorkItem.upsertPlaceOfSafety(
              incidentWorkItemId,
              formId,
              body,
            );
            if (response.status !== 204) {
              throw Error(JSON.stringify(response.data.message));
            }
          }}
          onUpdateSection={async (
            incidentWorkItemId,
            formId,
            sectionId,
            data,
            validateData,
            status,
          ) => {
            const response = await api.incidentWorkItem.updateData(
              incidentWorkItemId,
              formId,
              sectionId,
              data,
              validateData,
              status,
            );
            if (response.status !== 204) {
              throw Error(JSON.stringify(response.data.message));
            }
          }}
          onNavigateToReview={(incidentWorkItemId) => {
            navigate(
              createRoute.incidentReviewPlacesOfSafetyPage(incidentWorkItemId),
            );
          }}
        />
      )}
    </>
  );
}

export type UpsertPlaceOfSafetyRecordPageInnerProps = {
  incidentWorkItem: IncidentWorkItemWithContext;
  refetch: () => Promise<void>;
  edit?: boolean;
  existingFormId?: IncidentFormId;
  /** Handler to create/update a place of safety form atomically */
  onUpsertPlaceOfSafety: (
    incidentWorkItemId: IncidentWorkItemId,
    formId: IncidentFormId,
    body: {
      createForm: boolean;
      mainSectionData: Record<string, unknown>;
      mainSectionValidateData: boolean;
      arrivalSectionData?: Record<string, unknown> | null;
      arrivalSectionValidateData?: boolean;
      arrivalStatus?: IncidentWorkItemStatus;
      leaveSectionData?: Record<string, unknown> | null;
      leaveSectionValidateData?: boolean;
    },
  ) => Promise<void>;
  /** Handler to update a form section */
  onUpdateSection: (
    incidentWorkItemId: IncidentWorkItemId,
    formId: IncidentFormId,
    sectionId: FormSection,
    data: Record<string, unknown>,
    validateData: boolean,
    status?: IncidentWorkItemStatus,
  ) => Promise<void>;
  /** Handler called when form is complete to navigate to review page */
  onNavigateToReview: (incidentWorkItemId: IncidentWorkItemId) => void;
};

/**
 * This form captures the values for multiple form sections before
 * submitting them all at once. This is an attempt to make the journey
 * "atomic" and to minimise the impact of the user navigating away from
 * the page before completing all sections.
 */
export function UpsertPlaceOfSafetyRecordPageInner({
  incidentWorkItem,
  refetch,
  edit,
  existingFormId,
  onUpsertPlaceOfSafety,
  onUpdateSection,
  onNavigateToReview,
}: UpsertPlaceOfSafetyRecordPageInnerProps) {
  const toastNotifications = useToastNotifications();
  const { t } = useTranslation();

  const incidentWorkItemId = incidentWorkItem.incidentWorkItem.id;
  const workflowVersion = incidentWorkItem.incidentWorkItem
    .workflowVersion as WorkflowVersion;

  const recordPlaceOfSafetySections =
    workflows[workflowVersion]?.recordPlaceOfSafety.formDefinition.sections;
  if (recordPlaceOfSafetySections === undefined) {
    throw new Error("Workflow not found");
  }

  // Determine if we should skip the leave step based on triage outcome
  const triageOutcome = getSupportedIncidentTypeSelection({
    incidentWorkItem,
  })
    // .orTee((e) => {
    //   console.error("Unsupported incident type", e.prettifyError());
    // })
    .unwrapOr(undefined);
  const shouldSkipLeave = match(triageOutcome)
    .with("mca", () => true)
    .with("136", () => false)
    .with(undefined, () => false)
    .exhaustive();

  // This is where we store the data for the three form sections
  const [posMainSection, setPosMainSection] = useState<Record<
    string,
    unknown
  > | null>(null);
  const [posArriveSection, setPosArriveSection] = useState<Record<
    string,
    unknown
  > | null>(null);

  // This is the current form section the user is completing
  const [currentFormSection, setCurrentFormSection] = useState<
    "main" | "arrive" | "leave"
  >("main");

  const existingForm = useMemo(
    () =>
      edit && existingFormId
        ? incidentWorkItem.forms.find((f) => f.id === existingFormId)
        : undefined,
    [edit, existingFormId, incidentWorkItem.forms],
  );

  const mainSectionSchema = useMemo(
    () =>
      recordPlaceOfSafetySections
        .find(
          (section) =>
            section.id === FormSectionSchema.enum.recordPlaceOfSafetyMain,
        )
        ?.buildFormSpecification({ incidentWorkItem }).formSchema,
    [recordPlaceOfSafetySections, incidentWorkItem],
  );
  if (!mainSectionSchema) {
    throw new Error("Schema not found");
  }

  const existingMainSectionData = useMemo(
    () =>
      existingForm?.sections.find(
        (section) =>
          section.id === FormSectionSchema.enum.recordPlaceOfSafetyMain,
      )?.data,
    [existingForm?.sections],
  );
  const existingMainSectionDataIsValid = mainSectionSchema.safeParse(
    existingMainSectionData,
  ).success;

  const arriveSectionSchema = useMemo(
    () =>
      recordPlaceOfSafetySections
        .find(
          (section) =>
            section.id === FormSectionSchema.enum.recordPlaceOfSafetyArrival,
        )
        ?.buildFormSpecification({ incidentWorkItem }).formSchema,
    [recordPlaceOfSafetySections, incidentWorkItem],
  );
  if (!arriveSectionSchema) {
    throw new Error("Schema not found");
  }
  const existingArriveSectionData = useMemo(
    () =>
      existingForm?.sections.find(
        (section) =>
          section.id === FormSectionSchema.enum.recordPlaceOfSafetyArrival,
      )?.data,
    [existingForm?.sections],
  );
  const existingArriveSectionDataIsValid = arriveSectionSchema.safeParse(
    existingArriveSectionData,
  ).success;

  const leaveSectionSchema = useMemo(
    () =>
      recordPlaceOfSafetySections
        .find(
          (section) =>
            section.id === FormSectionSchema.enum.recordPlaceOfSafetyLeave,
        )
        ?.buildFormSpecification({ incidentWorkItem }).formSchema,
    [recordPlaceOfSafetySections, incidentWorkItem],
  );
  if (!leaveSectionSchema) {
    throw new Error("Schema not found");
  }

  const existingLeaveSectionData = useMemo(
    () =>
      existingForm?.sections.find(
        (section) =>
          section.id === FormSectionSchema.enum.recordPlaceOfSafetyLeave,
      )?.data,
    [existingForm?.sections],
  );
  const existingLeaveSectionDataIsValid = leaveSectionSchema.safeParse(
    existingLeaveSectionData,
  ).success;

  // These are the handlers for completing each form section
  const handleFormComplete = useCallback(() => {
    void refetch();
    onNavigateToReview(incidentWorkItemId);
  }, [incidentWorkItemId, onNavigateToReview, refetch]);

  const handleMainSectionComplete = useCallback(
    // eslint-disable-next-line @typescript-eslint/require-await
    async (values: Record<string, unknown>) => {
      setPosMainSection(values);
      setCurrentFormSection("arrive");
    },
    [],
  );

  const canHandleMainSectionAutoSave =
    edit &&
    existingFormId &&
    (recordPlaceOfSafetySections.find(
      (section) =>
        section.id === FormSectionSchema.enum.recordPlaceOfSafetyMain,
    )?.autoSave ??
      false);
  const handleMainSectionAutoSave: Exclude<
    RecordPlaceOfSafetyFormProps<typeof workflowVersion>["onAutoSave"],
    undefined
  > = useCallback(
    (values) => {
      if (!existingFormId) {
        return errAsync("Missing form id");
      }

      if (existingMainSectionDataIsValid) {
        // Because we started off with a valid and complete form, we should not regress
        // to a partial form.
        const isValid = mainSectionSchema.safeParse(values).success;
        if (!isValid) {
          return errAsync("Blocked auto-save: form is not valid");
        }
      }

      return ResultAsync.fromPromise(
        onUpdateSection(
          incidentWorkItemId,
          existingFormId,
          FormSectionSchema.enum.recordPlaceOfSafetyMain,
          values,
          existingMainSectionDataIsValid,
        ),
        (error) =>
          error instanceof Error ? error.message : JSON.stringify(error),
      )
        .orTee(debug)
        .map(() => true as const);
    },
    [
      existingFormId,
      existingMainSectionDataIsValid,
      incidentWorkItemId,
      mainSectionSchema,
      onUpdateSection,
    ],
  );

  const handleArriveSectionOnBack = useCallback(() => {
    setCurrentFormSection("main");
  }, []);

  /**
   * Handles submission when the leave step is skipped (MCA journey).
   * This function mirrors handleLeaveSectionComplete but omits the leave section.
   */
  const handleSubmitWithoutLeave = useCallback(
    async (posArriveValues: Record<string, unknown>) => {
      try {
        if (posMainSection === null) {
          throw Error("Main section is missing and mandatory");
        }

        const formId =
          edit && existingFormId
            ? existingFormId
            : IncidentFormIdSchema.parse(v4());

        const didArriveAtPlaceOfSafety =
          (
            posArriveValues as Partial<RecordPlaceOfSafetyArrivalMinimumFields> | null
          )?.arrivalDateTime ?? undefined;

        await onUpsertPlaceOfSafety(incidentWorkItemId, formId, {
          createForm: !edit,
          mainSectionData: posMainSection,
          mainSectionValidateData: true,
          arrivalSectionData: posArriveValues,
          arrivalSectionValidateData: true,
          arrivalStatus:
            didArriveAtPlaceOfSafety !== undefined
              ? IncidentWorkItemStatusSchema.enum["arrived-at-place-of-safety"]
              : undefined,
        });

        await refetch();
        handleFormComplete();
      } catch (error) {
        debug("Failed to update Place of Safety sections", error);
        toastNotifications.show(
          t("incidentWorkItem.notificationFailedToSave"),
          {
            severity: "error",
          },
        );
      }
    },
    [
      posMainSection,
      edit,
      existingFormId,
      refetch,
      handleFormComplete,
      incidentWorkItemId,
      toastNotifications,
      t,
      onUpsertPlaceOfSafety,
    ],
  );

  const handleArriveSectionComplete = useCallback(
    async (values: Record<string, unknown>) => {
      if (shouldSkipLeave) {
        // MCA journey: submit directly without leave step
        await handleSubmitWithoutLeave(values);
      } else {
        // Standard journey: proceed to leave step
        setPosArriveSection(values);
        setCurrentFormSection("leave");
      }
    },
    [shouldSkipLeave, handleSubmitWithoutLeave],
  );

  const canHandleArriveSectionAutoSave =
    edit &&
    existingFormId &&
    (recordPlaceOfSafetySections.find(
      (section) =>
        section.id === FormSectionSchema.enum.recordPlaceOfSafetyArrival,
    )?.autoSave ??
      false);

  const handleArriveSectionAutoSave: Exclude<
    RecordPlaceOfSafetyFormProps<typeof workflowVersion>["onAutoSave"],
    undefined
  > = useCallback(
    (values) => {
      if (!existingFormId) {
        return errAsync("Missing form id");
      }

      if (existingArriveSectionDataIsValid) {
        // Because we started off with a valid and complete form, we should not regress
        // to a partial form.
        const isValid = arriveSectionSchema.safeParse(values).success;
        if (!isValid) {
          return errAsync("Blocked auto-save: form is not valid");
        }
      }

      return ResultAsync.fromPromise(
        onUpdateSection(
          incidentWorkItemId,
          existingFormId,
          FormSectionSchema.enum.recordPlaceOfSafetyArrival,
          values,
          existingArriveSectionDataIsValid,
        ),
        (error) =>
          error instanceof Error ? error.message : JSON.stringify(error),
      )
        .orTee(debug)
        .map(() => true as const);
    },
    [
      arriveSectionSchema,
      existingArriveSectionDataIsValid,
      existingFormId,
      incidentWorkItemId,
      onUpdateSection,
    ],
  );

  const handleLeaveSectionOnBack = useCallback(() => {
    setCurrentFormSection("arrive");
  }, []);

  const handleLeaveSectionComplete = useCallback(
    async (posLeaveSection: Record<string, unknown>) => {
      try {
        if (posMainSection === null) {
          throw Error("Main section is missing and mandatory");
        }

        const formId =
          edit && existingFormId
            ? existingFormId
            : IncidentFormIdSchema.parse(v4());

        const didArriveAtPlaceOfSafety =
          posArriveSection !== null
            ? ((
                posArriveSection as Partial<RecordPlaceOfSafetyArrivalMinimumFields> | null
              )?.arrivalDateTime ?? undefined)
            : undefined;

        await onUpsertPlaceOfSafety(incidentWorkItemId, formId, {
          createForm: !edit,
          mainSectionData: posMainSection,
          mainSectionValidateData: true,
          arrivalSectionData: posArriveSection,
          arrivalSectionValidateData: true,
          arrivalStatus:
            didArriveAtPlaceOfSafety !== undefined
              ? IncidentWorkItemStatusSchema.enum["arrived-at-place-of-safety"]
              : undefined,
          leaveSectionData: posLeaveSection,
          leaveSectionValidateData: true,
        });

        await refetch();
        handleFormComplete();
      } catch (error) {
        debug("Failed to update Place of Safety sections", error);
        toastNotifications.show(
          t("incidentWorkItem.notificationFailedToSave"),
          {
            severity: "error",
          },
        );
      }
    },
    [
      posMainSection,
      edit,
      existingFormId,
      posArriveSection,
      refetch,
      handleFormComplete,
      incidentWorkItemId,
      toastNotifications,
      t,
      onUpsertPlaceOfSafety,
    ],
  );

  const canHandleLeaveSectionAutoSave =
    edit &&
    existingFormId &&
    (recordPlaceOfSafetySections.find(
      (section) =>
        section.id === FormSectionSchema.enum.recordPlaceOfSafetyLeave,
    )?.autoSave ??
      false);

  const handleLeaveSectionAutoSave: Exclude<
    RecordPlaceOfSafetyFormProps<typeof workflowVersion>["onAutoSave"],
    undefined
  > = useCallback(
    (values: Partial<Record<string, unknown>>) => {
      if (!existingFormId) {
        return errAsync("Missing form id");
      }

      if (existingLeaveSectionDataIsValid) {
        // Because we started off with a valid and complete form, we should not regress
        // to a partial form.
        const isValid = leaveSectionSchema.safeParse(values).success;
        if (!isValid) {
          return errAsync("Blocked auto-save: form is not valid");
        }
      }

      return ResultAsync.fromPromise(
        onUpdateSection(
          incidentWorkItemId,
          existingFormId,
          FormSectionSchema.enum.recordPlaceOfSafetyLeave,
          values as Record<string, unknown>,
          existingLeaveSectionDataIsValid,
        ),
        (error) =>
          error instanceof Error ? error.message : JSON.stringify(error),
      )
        .orTee(debug)
        .map(() => true as const);
    },
    [
      existingFormId,
      existingLeaveSectionDataIsValid,
      incidentWorkItemId,
      leaveSectionSchema,
      onUpdateSection,
    ],
  );

  // We will chain the three forms together, so we need to make sure that they
  // are created and kept mounted, even if we don't display them all the time.
  // This should ensure that they maintain state about what page they are on
  // and what the history of the pages visited. If we unmounted them we would
  // lose this state, so we use a conditional { display: "none" } instead.
  return (
    <Box>
      <Box sx={{ display: currentFormSection === "main" ? undefined : "none" }}>
        <RecordPlaceOfSafetyForm
          onAutoSave={
            canHandleMainSectionAutoSave ? handleMainSectionAutoSave : undefined
          }
          onSubmit={handleMainSectionComplete}
          onCancel={handleFormComplete}
          workflowVersion={workflowVersion}
          initialValues={edit ? existingMainSectionData : undefined}
          incidentWorkItem={incidentWorkItem}
        />
      </Box>
      <Box
        sx={{ display: currentFormSection === "arrive" ? undefined : "none" }}
      >
        <ArrivePlaceOfSafetyForm
          onAutoSave={
            canHandleArriveSectionAutoSave
              ? handleArriveSectionAutoSave
              : undefined
          }
          onSubmit={handleArriveSectionComplete}
          onBack={handleArriveSectionOnBack}
          onCancel={handleFormComplete}
          workflowVersion={workflowVersion}
          initialValues={edit ? existingArriveSectionData : undefined}
          incidentWorkItem={incidentWorkItem}
        />
      </Box>
      {!shouldSkipLeave && (
        <Box
          sx={{ display: currentFormSection === "leave" ? undefined : "none" }}
        >
          <LeavePlaceOfSafetyForm
            onAutoSave={
              canHandleLeaveSectionAutoSave
                ? handleLeaveSectionAutoSave
                : undefined
            }
            onSubmit={handleLeaveSectionComplete}
            onBack={handleLeaveSectionOnBack}
            onCancel={handleFormComplete}
            workflowVersion={workflowVersion}
            initialValues={edit ? existingLeaveSectionData : undefined}
            incidentWorkItem={incidentWorkItem}
          />
        </Box>
      )}
    </Box>
  );
}
